import { useEffect, useState } from 'react';
import { HOME_PATH } from '@/lib/paths';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { useUserRole } from '@/hooks/useUserRole';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Users, FileText, Settings, BarChart3, Sparkles, Music, Moon, BookOpen, Wand2, Shield, GraduationCap, CreditCard, Stars, Percent, Package, MessageSquare, AlertTriangle, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import AdminContentManager from '@/components/AdminContentManager';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AnalyticsDashboard from '@/components/admin/AnalyticsDashboard';
import OracleCardsManager from '@/components/admin/OracleCardsManager';
import MeditationsManager from '@/components/admin/MeditationsManager';
import CoursesManager from '@/components/admin/CoursesManager';
import KaleidoscopeManager from '@/components/admin/KaleidoscopeManager';
import TarotCardsManager from '@/components/admin/TarotCardsManager';
import TarotEducationManager from '@/components/admin/TarotEducationManager';
import FraudMonitoring from '@/components/admin/FraudMonitoring';
import WellnessEducationManager from '@/components/admin/WellnessEducationManager';
import SubscriptionTiersManager from '@/components/admin/SubscriptionTiersManager';
import ManifestationTemplatesManager from '@/components/admin/ManifestationTemplatesManager';
import UserRolesManager from '@/components/admin/UserRolesManager';
import DiscountCodesManager from '@/components/admin/DiscountCodesManager';
import FeaturePricingManager from '@/components/admin/FeaturePricingManager';
import BundlesManager from '@/components/admin/BundlesManager';
import { TherapyModeAnalytics } from '@/components/admin/TherapyModeAnalytics';
import SafetyReportsManager from '@/components/admin/SafetyReportsManager';
import { AdminImpersonation } from '@/components/admin/AdminImpersonation';

const AdminPortal = () => {
  const [user, setUser] = useState<User | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [pendingReportsCount, setPendingReportsCount] = useState(0);
  const { isAdmin, isLoading, roles } = useUserRole(user);
  const navigate = useNavigate();

  useEffect(() => {
    const loadUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      setUserLoading(false);
    };
    loadUser();
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    
    const loadPendingReportsCount = async () => {
      const { count } = await supabase
        .from('adverse_events')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');
      
      setPendingReportsCount(count || 0);
    };

    loadPendingReportsCount();
    
    // Refresh count every 30 seconds
    const interval = setInterval(loadPendingReportsCount, 30000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  // Wait for both user and roles to load before checking admin status
  const combinedLoading = userLoading || isLoading;

  // Redirect if not admin (only after loading is complete and roles are checked)
  useEffect(() => {
    if (!combinedLoading && !isAdmin && user && roles.length > 0) {
      navigate(HOME_PATH);
    }
  }, [isAdmin, combinedLoading, navigate, user, roles]);

  if (combinedLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-foreground">Admin Portal</h1>
              </div>
            </div>
            
            <Link to="/dashboard">
              <Button variant="outline" size="sm">
                Back to App
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Tabs defaultValue="content" className="w-full">
          <TabsList className="flex overflow-x-auto scrollbar-hide w-full gap-1 mb-4 sm:mb-6 p-1 justify-start">
            <TabsTrigger value="impersonate" className="text-xs shrink-0 px-2.5 gap-1.5">
              <Eye className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">View As</span>
            </TabsTrigger>
            <TabsTrigger value="content" className="text-xs shrink-0 px-2.5 gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Content</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="text-xs shrink-0 px-2.5 gap-1.5">
              <BarChart3 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Analytics</span>
            </TabsTrigger>
            <TabsTrigger value="therapy-modes" className="text-xs shrink-0 px-2.5 gap-1.5">
              <MessageSquare className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Therapy</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="text-xs shrink-0 px-2.5 gap-1.5">
              <Users className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger value="discounts" className="text-xs shrink-0 px-2.5 gap-1.5">
              <Percent className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Discounts</span>
            </TabsTrigger>
            <TabsTrigger value="pricing" className="text-xs shrink-0 px-2.5 gap-1.5">
              <CreditCard className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Pricing</span>
            </TabsTrigger>
            <TabsTrigger value="bundles" className="text-xs shrink-0 px-2.5 gap-1.5">
              <Package className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Bundles</span>
            </TabsTrigger>
            <TabsTrigger value="education" className="text-xs shrink-0 px-2.5 gap-1.5">
              <GraduationCap className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Education</span>
            </TabsTrigger>
            <TabsTrigger value="oracle" className="text-xs shrink-0 px-2.5 gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Oracle</span>
            </TabsTrigger>
            <TabsTrigger value="tarot" className="text-xs shrink-0 px-2.5 gap-1.5">
              <Wand2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Tarot</span>
            </TabsTrigger>
            <TabsTrigger value="meditations" className="text-xs shrink-0 px-2.5 gap-1.5">
              <Music className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Meditations</span>
            </TabsTrigger>
            <TabsTrigger value="courses" className="text-xs shrink-0 px-2.5 gap-1.5">
              <BookOpen className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Courses</span>
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="text-xs shrink-0 px-2.5 gap-1.5">
              <CreditCard className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Subs</span>
            </TabsTrigger>
            <TabsTrigger value="kaleidoscope" className="text-xs shrink-0 px-2.5 gap-1.5">
              <Moon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Kaleidoscope</span>
            </TabsTrigger>
            <TabsTrigger value="safety" className="text-xs shrink-0 px-2.5 gap-1.5 relative">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Safety</span>
              {pendingReportsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                  {pendingReportsCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="security" className="text-xs shrink-0 px-2.5 gap-1.5">
              <Shield className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Security</span>
            </TabsTrigger>
            <TabsTrigger value="manifestations" className="text-xs shrink-0 px-2.5 gap-1.5">
              <Stars className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Manifest</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="impersonate">
            <AdminImpersonation />
          </TabsContent>

          <TabsContent value="content">
            <Card className="p-4 sm:p-6">
              <AdminContentManager />
            </Card>
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsDashboard />
          </TabsContent>

          <TabsContent value="therapy-modes">
            <TherapyModeAnalytics />
          </TabsContent>

          <TabsContent value="users">
            <Card className="p-4 sm:p-6">
              <UserRolesManager />
            </Card>
          </TabsContent>

          <TabsContent value="discounts">
            <Card className="p-4 sm:p-6">
              <DiscountCodesManager />
            </Card>
          </TabsContent>

          <TabsContent value="pricing">
            <Card className="p-4 sm:p-6">
              <FeaturePricingManager />
            </Card>
          </TabsContent>

          <TabsContent value="bundles">
            <Card className="p-4 sm:p-6">
              <BundlesManager />
            </Card>
          </TabsContent>

          <TabsContent value="education">
            <Card className="p-4 sm:p-6">
              <WellnessEducationManager />
            </Card>
          </TabsContent>

          <TabsContent value="oracle">
            <Card className="p-4 sm:p-6">
              <OracleCardsManager />
            </Card>
          </TabsContent>

          <TabsContent value="tarot">
            <Tabs defaultValue="cards" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="cards">Tarot Cards</TabsTrigger>
                <TabsTrigger value="education">Education Content</TabsTrigger>
              </TabsList>
              <TabsContent value="cards">
                <TarotCardsManager />
              </TabsContent>
              <TabsContent value="education">
                <Card className="p-4 sm:p-6">
                  <TarotEducationManager />
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="meditations">
            <Card className="p-4 sm:p-6">
              <MeditationsManager />
            </Card>
          </TabsContent>

          <TabsContent value="courses">
            <Card className="p-4 sm:p-6">
              <CoursesManager />
            </Card>
          </TabsContent>

          <TabsContent value="subscriptions">
            <Card className="p-4 sm:p-6">
              <SubscriptionTiersManager />
            </Card>
          </TabsContent>

          <TabsContent value="kaleidoscope">
            <Card className="p-4 sm:p-6">
              <KaleidoscopeManager />
            </Card>
          </TabsContent>

          <TabsContent value="safety">
            <SafetyReportsManager />
          </TabsContent>

          <TabsContent value="security">
            <FraudMonitoring />
          </TabsContent>

          <TabsContent value="manifestations">
            <Card className="p-4 sm:p-6">
              <ManifestationTemplatesManager />
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPortal;
